// Super-Admin Assignments page — assign consultant + agent to each property in one place
import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, MapPin, Save, Users, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { PROPERTY_TYPE_LABEL } from '@/lib/constants';

const NONE = '_none_';

export default function AdminAssignments() {
  const [props, setProps] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [agents, setAgents] = useState([]);
  const [dirty, setDirty] = useState({});       // { propId: { consultant_id, agent_id } }
  const [saving, setSaving] = useState(null);   // propId in flight

  const load = useCallback(async () => {
    try {
      const [pr, cr, ar] = await Promise.all([
        api.get('/properties'), api.get('/consultants'), api.get('/agents'),
      ]);
      setProps(pr.data); setConsultants(cr.data); setAgents(ar.data);
    } catch (e) { toast.error('Failed to load assignments'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (pid, key, value) => {
    setDirty((d) => ({ ...d, [pid]: { ...(d[pid] || {}), [key]: value === NONE ? null : value } }));
  };

  const save = async (p) => {
    const changes = dirty[p.id];
    if (!changes) return;
    setSaving(p.id);
    try {
      if ('consultant_id' in changes && changes.consultant_id !== p.consultant_id) {
        await api.post(`/properties/${p.id}/assign-consultant`, null, { params: { consultant_id: changes.consultant_id || '' } });
      }
      if ('agent_id' in changes && changes.agent_id !== p.agent_id) {
        await api.post(`/properties/${p.id}/assign-agent`, null, { params: { agent_id: changes.agent_id || '' } });
      }
      toast.success(`Assignments saved for ${p.name}`);
      setDirty((d) => { const c = { ...d }; delete c[p.id]; return c; });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(null); }
  };

  const currentVal = (p, key) => dirty[p.id]?.[key] !== undefined ? (dirty[p.id][key] || '') : (p[key] || '');
  const hasDirty = (p) => {
    const d = dirty[p.id];
    if (!d) return false;
    return ('consultant_id' in d && (d.consultant_id || null) !== (p.consultant_id || null)) ||
           ('agent_id' in d && (d.agent_id || null) !== (p.agent_id || null));
  };

  return (
    <div>
      <PageHeader title="Assignments" subtitle="Assign / reassign consultants and agents across all properties. Audit-logged." />

      <Card className="p-0 overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-muted/60">
            <tr>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Property</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Type</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">City</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground"><Shield /> Consultant</th>
              <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground"><Brief /> Agent</th>
              <th className="px-3 py-2 text-xs uppercase text-muted-foreground text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {props.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-12 text-center text-muted-foreground">No properties yet.</td></tr>
            )}
            {props.map((p) => (
              <tr key={p.id} className={`border-t ${hasDirty(p) ? 'bg-amber-50/40' : ''}`} data-testid={`assignment-row-${p.id}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{p.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{p.code}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2"><Badge variant="outline" className="capitalize">{PROPERTY_TYPE_LABEL[p.type] || p.type}</Badge></td>
                <td className="px-3 py-2 text-xs"><MapPin className="h-3.5 w-3.5 inline mr-1 text-muted-foreground" />{p.city || '—'}</td>
                <td className="px-3 py-2 min-w-[200px]">
                  <Select value={currentVal(p, 'consultant_id') || NONE} onValueChange={(v) => set(p.id, 'consultant_id', v)}>
                    <SelectTrigger data-testid={`consultant-select-${p.id}`} className="h-9"><SelectValue placeholder="— None —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— None —</SelectItem>
                      {consultants.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.firm_name ? ` · ${c.firm_name}` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2 min-w-[200px]">
                  <Select value={currentVal(p, 'agent_id') || NONE} onValueChange={(v) => set(p.id, 'agent_id', v)}>
                    <SelectTrigger data-testid={`agent-select-${p.id}`} className="h-9"><SelectValue placeholder="— None —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— None —</SelectItem>
                      {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{a.firm_name ? ` · ${a.firm_name}` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" disabled={!hasDirty(p) || saving === p.id} onClick={() => save(p)} data-testid={`save-assignment-${p.id}`}>
                    <Save className="h-3.5 w-3.5 mr-1" /> {saving === p.id ? 'Saving…' : 'Save'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// Tiny icon helpers so we don't depend on more imports
function Shield()  { return <Users className="h-3 w-3 inline mr-1" />; }
function Brief()   { return <Briefcase className="h-3 w-3 inline mr-1" />; }
